import {createSelector} from 'reselect'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  onlyIfForm,
  useSelector,
} from '../../../store.js'
import {
  PluralBlock,
  Plural,
  Count,
  plural,
} from '../../../common/components/Plural.js'
import apiverson from '../../../common/apiverson.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {setStocktake} from '../../../data/stocktakes.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {currentPageSelector} from '../../../redux/selectors/ui/index.js'
import {PRODUCT_LIST_PAGE} from '../../../common/constants/Pages.js'
import StocktakeFilter from '../../../common/components/StocktakeFilter.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {isPresent} from '../../../common/utils.js'
import {refreshProductList} from '../../ProductListPage/productListFunctions.js'
import Icon from '../../../common/icons/Icon.js'

const MODAL_FORM = 'remove_from_stocktake_modal'

export async function showRemoveFromStocktakeModal(skus, referenceID) {
  setForm(MODAL_FORM, {
    referenceID,
    skus,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({skus, referenceID}) => {
    const errors = {}

    if (!isPresent(referenceID)) {
      errors.referenceID = 'Session ID is required'
      errors.preventSave = true
    }

    if (skus.length === 0) {
      errors.preventSave = true
    }

    return errors
  },
)

export async function removeFromStocktake() {
  try {
    const {referenceID, skus} = modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    const {json: stocktake} = await apiverson.delete(
      `/stocktake/${encodeURIComponent(referenceID)}/line`,
      {
        criteria: {sku: skus},
      },
    )

    setStocktake(stocktake)

    closeModal()

    showMessageToast(
      plural(skus.length)`Removed products${['s']} from stocktake.`,
    )

    if (currentPageSelector(getState()) === PRODUCT_LIST_PAGE) {
      await refreshProductList()
    }
  } catch (err) {
    updateModalForm({
      serverError: `Error removing products from stocktake: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

function RemoveFromStocktakeModal({form}) {
  const errors = useSelector(errorsSelector)
  const totalToBeRemoved = form.skus.length
  const canRemoveFromStocktake = totalToBeRemoved > 0

  return (
    <ConfirmModal
      title={plural(totalToBeRemoved)`Remove Product${['s']} from Stocktake`}
      size="sm-md"
      onConfirm={canRemoveFromStocktake ? () => removeFromStocktake() : null}
      onCancel={() => closeModal()}
      confirmText="Remove"
      cancelText={canRemoveFromStocktake ? 'Cancel' : 'Close'}
      isSaving={form.isSaving}
      isDisabled={errors.preventSave || form.isLoading}
      error={form.serverError}
    >
      {canRemoveFromStocktake && form.referenceID ? (
        <p className="fs-01 margin-bottom-10">
          <PluralBlock count={totalToBeRemoved}>
            <strong>
              Are you sure you want to remove <Count />{' '}
              <Plural word="product" /> from this stocktake?
            </strong>
          </PluralBlock>
        </p>
      ) : form.referenceID ? (
        <p className="fs-00 alert alert--warning margin-bottom-10">
          <Icon
            name="exclamation-triangle"
            className="text--warning-orange fs-01 lh-sm v-align-middle margin-right-5"
          />
          No products were included in your selection.
        </p>
      ) : null}
      {canRemoveFromStocktake && (
        <div className="row">
          <div className="medium-9 columns">
            <PluralBlock count={totalToBeRemoved}>
              <p className="fs-00 margin-bottom-10">
                <strong>
                  Which stocktake do you want to remove{' '}
                  <Plural p="products" s="this product" /> from?
                </strong>
              </p>
              {form.referenceID ? (
                <>
                  <p className="fs-01 margin-bottom-0">
                    <strong>{form.referenceID}</strong>
                  </p>
                  <ButtonLink
                    className="fs-00"
                    id="REMOVE_FROM_STOCKTAKE_STOCKTAKE_FILTER__input"
                    onClick={() => updateModalForm({referenceID: null})}
                  >
                    Select a different stocktake
                  </ButtonLink>
                </>
              ) : (
                <>
                  <StocktakeFilter
                    dropdown="REMOVE_FROM_STOCKTAKE_STOCKTAKE_FILTER"
                    onSelect={(stocktake) => {
                      updateModalForm({referenceID: stocktake.reference_id})
                    }}
                    autoFocus
                  />
                </>
              )}
            </PluralBlock>
          </div>
        </div>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(RemoveFromStocktakeModal, modalFormSelector)
